import Select, { SelectItem } from '@components/v2/Select';
import {
  Box,
  Flex,
  Input,
  InputDescription,
  InputLabel,
  PhoneNumberInput,
  Text,
} from '@huspy/briks-web';
import { Divider } from '@huspy/styled-system/jsx';
import { UseFormReturnType } from '@mantine/form';
import { IS_UAE_ENV } from '@modules/core/utils';
import briksTheme from '@shared/briks-theme';
import { useTranslation } from 'react-i18next';

export const SectionHeader = ({
  title,
  Icon,
}: {
  title: string;
  Icon: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
}) => (
  <Flex justify='center' align='center' my='4'>
    <Flex align='center' justify='center'>
      <Flex align='center' justify='center' h='100%' w='100%' mr='2'>
        <Icon color={ briksTheme.colors['neutral.600'] } />
      </Flex>
      <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap'>
        {title}
      </Text>
    </Flex>
    <Divider />
  </Flex>
);

type CaseInputProps<T> = {
  form: UseFormReturnType<T, (values: T) => T>;
  name: string;
  placeholder?: string;
  label?: string;
  type?: 'text' | 'number' | 'date' | 'phone';
  trail?: string;
  disabled?: boolean;
  w?: '50%' | '100%';
};

export const CaseFormInput = <T, _>({
  form,
  name,
  label,
  placeholder,
  type = 'text',
  trail,
  disabled = false,
  ...rest
}: CaseInputProps<T>) => {
  const { t } = useTranslation();
  const { value, error, onChange } = form.getInputProps(name);
  const isError = !!error;
  const labelText = label ? t(`clients.formLabels.${label}`) : '';
  const errorText = error ?? '';

  // Helper function to render input label and description
  const renderInputLabel = () =>
    (label ? <InputLabel isError={ isError }>{labelText}</InputLabel> : <Box h='22px' />);
  const renderInputDescription = () => (
    <InputDescription mt='1' isError={ isError }>
      {errorText}
    </InputDescription>
  );

  const formatValue = () => {
    if (value === undefined || value === '') return '';
    if (value === 0) return 0;
    return Number(value).toLocaleString(IS_UAE_ENV ? 'en-US' : 'es-ES');
  };

  // Shared props for Input components
  const sharedInputProps = {
    id: name,
    name,
    disabled,
    placeholder: placeholder ?? labelText,
    isError,
    ...rest,
  };

  // Input type-specific handlers/components
  const inputComponents: Record<string, JSX.Element> = {
    phone: (
      <PhoneNumberInput
        { ...sharedInputProps }
        defaultCountry={ t('common.forms.mobile.defaultCountryPrefix') }
        placeholder={ t('common.forms.mobile.placeholder') }
        onChange={ (e) => {
          onChange(e.phone);
          if (e.inputValue.length > 0) {
            form.clearFieldError(name);
          }
        } }
        value={ value || '' }
      />
    ),
    number: (
      <Input
        { ...sharedInputProps }
        type='text'
        value={ formatValue() }
        onChange={ (e) => {
          const { value: stringValue } = e.target;
          const cleanedStringValue = stringValue.replaceAll(/\D/g, '');
          if (cleanedStringValue === '') {
            onChange();
          } else {
            onChange(Number(cleanedStringValue));
          }
        } }
        onWheel={ (e) => (e.target as HTMLInputElement).blur() }
        icon={ trail && (
          <Text size='md' color='neutral.400'>
            {trail}
          </Text>
        ) }
      />
    ),
    default: (
      <Input
        { ...sharedInputProps }
        type={ type }
        value={ value || '' }
        onChange={ (e) => onChange(e.target.value) }
        icon={ trail && (
          <Text size='md' color='neutral.400'>
            {trail}
          </Text>
        ) }
      />
    ),
  };

  // Pick the appropriate input component based on type
  const selectedInput = inputComponents[type] || inputComponents.default;

  return (
    <Box w='50%'>
      {renderInputLabel()}
      {selectedInput}
      {renderInputDescription()}
    </Box>
  );
};

type CaseSelectInputProps<T> = CaseInputProps<T> & {
  data: SelectItem[];
};

export const CaseFormSelectInput = <T, _>({
  form,
  name,
  label,
  data,
  w = '50%',
  ...rest
}: CaseSelectInputProps<T>) => {
  const { t } = useTranslation();
  const { value } = form.getInputProps(name);
  const defaultValue = value ? [value] : [];

  return (
    <Box w={ w } id={ name }>
      <Select
        name={ name as string }
        label={ t(`clients.formLabels.${label}`) }
        data={ data }
        placeholder={ t(`clients.formLabels.${label}`) }
        onValueChange={ (e) => {
          form.getInputProps(name).onChange(e.value[0]);
        } }
        isError={ !!form.getInputProps(name)?.error }
        defaultValue={ defaultValue }
        { ...rest }
      />
      <InputDescription color='error.800' mt='1'>
        {form.getInputProps(name)?.error ?? ''}
      </InputDescription>
    </Box>
  );
};
